import { computed, onBeforeUnmount, shallowRef } from 'vue'
import type { Router } from 'vue-router'
import type { Store } from 'vuex'
import { useConsoleTourStore } from '@/stores/consoleTour'
import {
  addLink, addSection, allLinks, defaultSidebarLayout, normalizeLayout, removeLink, removeSection,
  renameSection, setEntries, setSectionCollapsed, setSectionItems, updateLink,
  type SidebarEntry, type SidebarLayout, type SidebarLink, type SidebarLinkOverrides, type SidebarSection
} from './sidebarLayout'
import { getNavCatalog, resolveEntryRouteName, type NavCatalogEntry } from './navCatalog'
import { isNavIconName } from './navIcons'
import { isNavColor, type NavColor } from './navColors'

export interface ResolvedSidebarLink {
  kind: 'link'
  id: string
  link: SidebarLink
  entry: NavCatalogEntry
  /** The route this link opens for the current user. */
  routeName: string
  label: string
  icon: string
  color: NavColor
  badge?: string
}

export interface ResolvedSidebarSection {
  kind: 'section'
  id: string
  section: SidebarSection
  /** Only the links the current user may open. */
  links: ResolvedSidebarLink[]
  /** True when the section holds links the user cannot currently see. */
  hasHiddenLinks: boolean
}

export type ResolvedSidebarEntry = ResolvedSidebarLink | ResolvedSidebarSection

/** Long enough that a burst of drags or renames goes out as one save. */
const SAVE_DEBOUNCE_MS = 600

/**
 * The current person's sidebar: what to render, and the operations Customize mode performs on it.
 *
 * Rendering always goes through the live access check, so a link to a page the person can no
 * longer open (permission revoked, feature turned off, page removed) is skipped -- in every mode --
 * while staying in the stored layout, ready to reappear where it was if access comes back.
 *
 * Edits apply to a local draft at once and are saved, debounced, through
 * `patchOrgUserPreferences`, which commits them to the store and rolls back if the save fails.
 */
export function useSidebarLayout (router: Router, store: Store<any>, { onSaveError }: { onSaveError: (_error: unknown) => void }) {
  const consoleTourStore = useConsoleTourStore()
  const catalog = getNavCatalog(router)

  // Edits not yet handed to the store.
  const draft = shallowRef<SidebarLayout | null>(null)
  let saveTimer: ReturnType<typeof setTimeout> | null = null

  const storedLayout = computed(() => normalizeLayout(store.state.orgUserPreferences?.sidebarLayout))

  /** The layout edits apply to: pending edits, else what is stored, else the default. */
  const editableLayout = computed(() => draft.value ?? storedLayout.value ?? defaultSidebarLayout())

  const isCustomized = computed(() => (draft.value ?? storedLayout.value) !== null)

  // The orientation tour points at the original sidebar items by their tour anchors. Showing the
  // default while it runs means a customized sidebar can never hide a step it relies on.
  const showsDefaultForTour = computed(() => consoleTourStore.isRunning)

  const layout = computed(() => showsDefaultForTour.value ? defaultSidebarLayout() : editableLayout.value)

  const resolveLink = (link: SidebarLink): ResolvedSidebarLink | null => {
    const entry = catalog.byId.get(link.target)
    // A fixed item is drawn above the layout already; a stored link to it is not drawn again.
    if (!entry || entry.fixed) {
      return null
    }
    return resolveEntry(link, entry)
  }

  const resolveEntry = (link: SidebarLink, entry: NavCatalogEntry): ResolvedSidebarLink | null => {
    const routeName = resolveEntryRouteName(entry, router, store)
    if (!routeName) {
      return null
    }
    const count = entry.badgeStateKey ? store.state[entry.badgeStateKey] : null
    return {
      kind: 'link',
      id: link.id,
      link,
      entry,
      routeName,
      label: link.label ?? entry.label,
      icon: isNavIconName(link.icon) ? link.icon : entry.icon,
      color: isNavColor(link.color) ? link.color : entry.color,
      badge: count != null ? String(count) : undefined
    }
  }

  const entries = computed<ResolvedSidebarEntry[]>(() => layout.value.entries.flatMap((entry): ResolvedSidebarEntry[] => {
    if (entry.kind === 'section') {
      const links = entry.items.map(resolveLink).filter((link): link is ResolvedSidebarLink => link !== null)
      return [{ kind: 'section', id: entry.id, section: entry, links, hasHiddenLinks: links.length < entry.items.length }]
    }
    const link = resolveLink(entry)
    return link ? [link] : []
  }))

  /** The items above every layout (Overview), that the person may open. */
  const fixedLinks = computed(() => catalog.entries
    .filter(entry => entry.fixed)
    .map(entry => resolveEntry({ kind: 'link', id: `fixed-${entry.id}`, target: entry.id }, entry))
    .filter((link): link is ResolvedSidebarLink => link !== null))

  /** Catalog entry ids already somewhere in the sidebar. */
  const addedTargets = computed(() => new Set(allLinks(editableLayout.value).map(link => link.target)))

  function flush () {
    if (saveTimer) {
      clearTimeout(saveTimer)
      saveTimer = null
    }
    const toSave = draft.value
    if (!toSave) {
      return
    }
    // The patch commits to the store straight away, so the draft is no longer needed to show it.
    draft.value = null
    store.dispatch('patchOrgUserPreferences', { sidebarLayout: toSave }).catch(onSaveError)
  }

  function update (change: (_layout: SidebarLayout) => SidebarLayout) {
    if (showsDefaultForTour.value) {
      return
    }
    draft.value = change(editableLayout.value)
    if (saveTimer) {
      clearTimeout(saveTimer)
    }
    saveTimer = setTimeout(flush, SAVE_DEBOUNCE_MS)
  }

  onBeforeUnmount(flush)

  const findStoredLink = (layout: SidebarLayout, id: string) => allLinks(layout).find(link => link.id === id)

  return {
    entries,
    fixedLinks,
    layout,
    isCustomized,
    addedTargets,
    catalog,
    flush,

    toggleSection (sectionId: string, collapsed: boolean) {
      update(layout => setSectionCollapsed(layout, sectionId, collapsed))
    },
    addLink (sectionId: string | null, target: string) {
      update(layout => addLink(layout, sectionId, target))
    },
    addSection (): string {
      let created = ''
      update(layout => {
        const result = addSection(layout)
        created = result.sectionId
        return result.layout
      })
      return created
    },
    renameSection (sectionId: string, title: string) {
      update(layout => renameSection(layout, sectionId, title))
    },
    updateLink (linkId: string, overrides: SidebarLinkOverrides) {
      update(layout => {
        const link = findStoredLink(layout, linkId)
        const entry = link && catalog.byId.get(link.target)
        if (!entry) {
          return layout
        }
        return updateLink(layout, linkId, overrides, { label: entry.label, icon: entry.icon, color: entry.color })
      })
    },
    removeLink (linkId: string) {
      update(layout => removeLink(layout, linkId))
    },
    removeSection (sectionId: string, keepItems: boolean) {
      update(layout => removeSection(layout, sectionId, { keepItems }))
    },
    reset () {
      draft.value = null
      if (saveTimer) {
        clearTimeout(saveTimer)
        saveTimer = null
      }
      store.dispatch('patchOrgUserPreferences', { sidebarLayout: null }).catch(onSaveError)
    },

    /**
     * Applies a new top-level order from drag and drop. `visible` is what was on screen, so
     * entries the person cannot see are not in it; they are kept, after the visible ones.
     */
    reorderTopLevel (visible: ResolvedSidebarEntry[]) {
      update(layout => {
        const sectionsById = new Map(layout.entries.filter(entry => entry.kind === 'section').map(entry => [entry.id, entry]))
        // A dragged link carries its stored copy. That matters for a drag between lists, which
        // arrives as two updates in either order: the source list may already have dropped it.
        const next = visible.map((entry): SidebarEntry => entry.kind === 'link' ? entry.link : sectionsById.get(entry.id) ?? entry.section)
        const placed = new Set(next.map(entry => entry.id))
        const shown = new Set(entries.value.map(entry => entry.id))
        const hidden = layout.entries.filter(entry => !placed.has(entry.id) && !shown.has(entry.id))
        // A link dragged out of a section is now at the top level; drop its old place if that
        // list has not been updated yet.
        const withoutMoved = [...next, ...hidden].map(entry => entry.kind === 'section'
          ? { ...entry, items: entry.items.filter(item => !placed.has(item.id)) }
          : entry)
        return setEntries(layout, withoutMoved)
      })
    },

    /** As `reorderTopLevel`, for the links in one section. */
    reorderSection (sectionId: string, visible: ResolvedSidebarLink[]) {
      update(layout => {
        const section = layout.entries.find((entry): entry is SidebarSection => entry.kind === 'section' && entry.id === sectionId)
        if (!section) {
          return layout
        }
        const next = visible.map(link => link.link)
        const placed = new Set(next.map(link => link.id))
        const shownSection = entries.value.find((entry): entry is ResolvedSidebarSection => entry.kind === 'section' && entry.id === sectionId)
        const shown = new Set(shownSection?.links.map(link => link.id) ?? [])
        const hidden = section.items.filter(link => !placed.has(link.id) && !shown.has(link.id))
        // Likewise for a link dragged in from the top level or another section.
        const withoutMoved = setEntries(layout, layout.entries
          .filter(entry => !(entry.kind === 'link' && placed.has(entry.id)))
          .map(entry => entry.kind === 'section' && entry.id !== sectionId
            ? { ...entry, items: entry.items.filter(item => !placed.has(item.id)) }
            : entry))
        return setSectionItems(withoutMoved, sectionId, [...next, ...hidden])
      })
    }
  }
}

export type SidebarLayoutController = ReturnType<typeof useSidebarLayout>
